import VendorsPage from './pages/VendorsPage';
import ExecutivePage from './pages/ExecutivePage';
import React from 'react';
import { HashRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SettingsProvider, useSettings } from './context/SettingsContext';
import FaviconUpdater from './FaviconUpdater';
import { ThemeModeProvider, useThemeMode } from './context/ThemeContext';
import BOQWorkbookPage from './pages/BOQWorkbookPage';

// Pages
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import LoginPage from './pages/LoginPage';
import DashboardLayout from './components/DashboardLayout';
import DashboardHome from './pages/DashboardHome';
import MaterialsPage from './pages/MaterialsPage';
import SitesPage from './pages/SitesPage';
import SiteDetailsPage from './pages/SiteDetailsPage';
import ReportsPage from './pages/ReportsPage';
import UsersPage from './pages/UsersPage';
import InventoryPage from './pages/InventoryPage';
import TransfersPage from './pages/TransfersPage';
import EmailSettingsPage from './pages/EmailSettingsPage';
import AdminSettingsPage from './pages/AdminSettingsPage';
import CompanySettingsPage from './pages/CompanySettingsPage';
import MyProfile from './pages/MyProfile';
import ChangeRequestsPage from './pages/ChangeRequestsPage';
import MyChangeRequestsPage from './pages/MyChangeRequestsPage';
import RaiseTicketPage from './pages/RaiseTicketPage'; 
import TicketsAdminPage from './pages/TicketsAdminPage'; 
import BOQPage from './pages/BOQPage';
import VendorComparisonPage from './pages/VendorComparisonPage';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <div>Loading...</div>;
  }

  return isAuthenticated ? children : <Navigate to="/login" />;
};

// Admin-only Route Component
const AdminRoute = ({ children }) => {
  const { user, isAuthenticated, loading } = useAuth();

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  return user?.role === 'admin' ? children : <Navigate to="/dashboard" replace />;
};

// Manager/Supervisor Route Component
const ManagerSupervisorRoute = ({ children }) => {
  const { user, isAuthenticated, loading } = useAuth();

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  return user?.role === 'manager' || user?.role === 'supervisor' ? children : <Navigate to="/dashboard" replace />;
};

function AppRoutes() {
  const { isAuthenticated } = useAuth();

  return (
    <Routes>
      <Route
        path="/login"
        element={isAuthenticated ? <Navigate to="/dashboard" /> : <LoginPage />}
      />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/dashboard" />} />
        <Route path="dashboard" element={<DashboardHome />} />
        <Route path="materials" element={<MaterialsPage />} />
        <Route path="sites" element={<SitesPage />} />
        <Route path="sites/:siteId" element={<SiteDetailsPage />} />
        <Route path="inventory" element={<InventoryPage />} />
        <Route path="transfers" element={<TransfersPage />} />
        <Route path="users" element={<UsersPage />} />
        <Route path="reports" element={<ReportsPage />} />
        <Route path="executive" element={<ExecutivePage />} />
        <Route path="profile" element={<MyProfile />} />
        <Route path="support" element={<RaiseTicketPage />} />
        <Route path="boq" element={<BOQPage />} />
        <Route path="boq-workbook" element={<BOQWorkbookPage />} />
        <Route path="vendor-comparison" element={<VendorComparisonPage />} />
<Route path="vendors" element={<VendorsPage />} />

        {/* Manager/Supervisor routes */}
        <Route path="my-change-requests" element={<ManagerSupervisorRoute><MyChangeRequestsPage /></ManagerSupervisorRoute>} />

        {/* Admin-only routes */}
        <Route path="admin/settings" element={<AdminRoute><AdminSettingsPage /></AdminRoute>} />
        <Route path="admin/email" element={<AdminRoute><EmailSettingsPage /></AdminRoute>} />
        <Route path="admin/change-requests" element={<AdminRoute><ChangeRequestsPage /></AdminRoute>} />
        <Route path="admin/tickets" element={<AdminRoute><TicketsAdminPage /></AdminRoute>} /> {/* ✅ NEW */}
        <Route path="settings" element={<AdminRoute><CompanySettingsPage /></AdminRoute>} />
      </Route>
      <Route path="*" element={<Navigate to="/dashboard" />} />
    </Routes>
  );
}

// App content with dynamic theme
function AppContent() {
  const { settings, loading } = useSettings();
  const { mode } = useThemeMode();

  if (loading) {
    return <div>Loading...</div>;
  }

  const theme = createTheme({
    palette: {
      mode,
      primary: {
        main: settings.primaryColor || '#64A8ED',
      },
      secondary: {
        main: settings.secondaryColor || '#C51668',
      },
      ...(mode === 'dark' ? {
        background: {
          default: '#121212',
          paper: '#1E1E1E',
        },
        text: {
          primary: '#FFFFFF',
          secondary: '#B0B0B0',
        }
      } : {
        background: {
          default: settings.backgroundColor || '#F5F5F5',
          paper: '#FFFFFF',
        }
      })
    },
    typography: {
      fontFamily: `${settings.primaryFont || 'Poppins'}, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif`,
      h1: { fontFamily: settings.headingFont || 'Inter' },
      h2: { fontFamily: settings.headingFont || 'Inter' },
      h3: { fontFamily: settings.headingFont || 'Inter' },
      h4: { fontFamily: settings.headingFont || 'Inter' },
      h5: { fontFamily: settings.headingFont || 'Inter' },
      h6: { fontFamily: settings.headingFont || 'Inter' },
    },
    components: {
      MuiAppBar: {
        styleOverrides: {
          root: { backgroundImage: 'none' }
        }
      },
      MuiDrawer: {
        styleOverrides: {
          paper: { backgroundImage: 'none' }
        }
      },
      MuiCard: {
        styleOverrides: {
          root: { backgroundImage: 'none' }
        }
      },
      MuiPaper: {
        styleOverrides: {
          root: { backgroundImage: 'none' }
        }
      }
    }
  });

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <FaviconUpdater />
      <AuthProvider>
        <Router>
          <AppRoutes />
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

function App() {
  return (
    <ThemeModeProvider>
    <SettingsProvider>
      <AppContent />
    </SettingsProvider>
    </ThemeModeProvider>
  );
}

export default App;